/** Scene-transition data model. A "cut" is the boundary between consecutive
 *  scenes (clip intervals); `afterSceneIndex` = i means the cut between scene
 *  i and scene i+1. Persisted as clip.data.transitions. The backend mirrors
 *  the validation in ../autoclipper-backend/src/lib/sceneTransitions.js —
 *  keep them in sync. */
export const TRANSITION_KINDS = [
  'camera-move',
  'zoom-in',
  'zoom-out',
  'whip-pan',
  'dip-to-black',
  'dip-to-white',
  'blur',
  'crossfade',
  'slide',
  'wipe',
] as const;

export type TransitionKind = (typeof TRANSITION_KINDS)[number];
export type TransitionDirection = 'left' | 'right' | 'up' | 'down';

export interface SceneTransition {
  /** The cut between scene `afterSceneIndex` and `afterSceneIndex + 1`. */
  afterSceneIndex: number;
  kind: TransitionKind;
  /** Clamped 0.1–1.5s; the layout additionally caps it against the
   *  neighboring scenes' lengths. */
  durationSec: number;
  /** slide / wipe / whip-pan only: the direction the incoming scene moves. */
  direction?: TransitionDirection;
}

/** A transition attached to a crop-keyframe boundary INSIDE a continuous
 *  interval (the CropCenterTrack row). Footage is continuous across the
 *  boundary, so only the transform family applies (no overlap kinds).
 *  `atTime` is the boundary SOURCE time in seconds (kf[i].endTime ≈
 *  kf[i+1].time), rounded to 3 decimals on write. Persisted as
 *  clip.data.keyframeTransitions. The backend mirrors the validation in
 *  ../autoclipper-backend/src/lib/sceneTransitions.js. */
export interface KeyframeTransition {
  atTime: number;
  kind: TransitionKind;
  durationSec: number;
  direction?: TransitionDirection;
}

export const DEFAULT_TRANSITION_DURATION_SEC = 0.2;
export const MIN_TRANSITION_DURATION_SEC = 0.1;
export const MAX_TRANSITION_DURATION_SEC = 1.5;

/** Overlap kinds blend two scenes and shorten the output; transform kinds
 *  animate around a hard cut and preserve duration. */
const OVERLAP_KINDS: ReadonlySet<TransitionKind> = new Set(['crossfade', 'slide', 'wipe']);
const DIRECTIONAL_KINDS: ReadonlySet<TransitionKind> = new Set(['slide', 'wipe', 'whip-pan']);
const DIRECTIONS: ReadonlySet<string> = new Set(['left', 'right', 'up', 'down']);

/** Kinds that animate around a hard cut without blending two clips — the only
 *  ones valid at a crop-keyframe boundary (footage is continuous there). This
 *  is exactly TRANSITION_KINDS minus the overlap family. */
export const TRANSFORM_TRANSITION_KINDS = TRANSITION_KINDS.filter(
  (k) => !OVERLAP_KINDS.has(k),
) as TransitionKind[];

export const isOverlapKind = (kind: TransitionKind): boolean => OVERLAP_KINDS.has(kind);
export const isDirectionalKind = (kind: TransitionKind): boolean => DIRECTIONAL_KINDS.has(kind);
export const isTransformKind = (kind: TransitionKind): boolean => !OVERLAP_KINDS.has(kind);
export const isValidDirection = (dir: unknown): dir is TransitionDirection =>
  typeof dir === 'string' && DIRECTIONS.has(dir);

export function clampTransitionDuration(sec: number): number {
  if (!Number.isFinite(sec) || sec <= 0) return DEFAULT_TRANSITION_DURATION_SEC;
  return Math.min(MAX_TRANSITION_DURATION_SEC, Math.max(MIN_TRANSITION_DURATION_SEC, sec));
}

/** Validate a persisted/unknown transitions blob: drop unknown kinds,
 *  out-of-range or duplicate cut indexes (first wins), clamp durations,
 *  keep `direction` only on directional kinds. Returns a sorted copy. */
export function sanitizeTransitions(raw: unknown, sceneCount: number): SceneTransition[] {
  if (!Array.isArray(raw)) return [];
  const seen = new Set<number>();
  const out: SceneTransition[] = [];
  for (const item of raw) {
    if (!item || typeof item !== 'object') continue;
    const t = item as Record<string, unknown>;
    const idx = t.afterSceneIndex;
    if (typeof idx !== 'number' || !Number.isInteger(idx) || idx < 0 || idx >= sceneCount - 1) continue;
    if (typeof t.kind !== 'string' || !(TRANSITION_KINDS as readonly string[]).includes(t.kind)) continue;
    if (seen.has(idx)) continue;
    seen.add(idx);
    const kind = t.kind as TransitionKind;
    const st: SceneTransition = {
      afterSceneIndex: idx,
      kind,
      durationSec: clampTransitionDuration(typeof t.durationSec === 'number' ? t.durationSec : NaN),
    };
    if (isDirectionalKind(kind) && typeof t.direction === 'string' && DIRECTIONS.has(t.direction)) {
      st.direction = t.direction as TransitionDirection;
    }
    out.push(st);
  }
  return out.sort((a, b) => a.afterSceneIndex - b.afterSceneIndex);
}
